import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { FileText, Package, Plus, Search, Users } from 'lucide-react';
import type { CustomerDto, InvoiceSummaryDto, Paginated, ProductDto } from '@portal/contracts';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { bk, useBusiness } from '@/lib/business';
import { get } from '@/lib/api';
import { useDebounced } from '@/lib/hooks';
import { money } from '@/lib/format';
import { NAV } from './nav';

/** Global quick navigation (⌘K / Ctrl+K): pages, plus server-side search across invoices, customers and products. */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const q = useDebounced(term.trim(), 250);
  const { businessId, can } = useBusiness();
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const enabled = open && q.length >= 2 && !!businessId;
  const invoices = useQuery({ queryKey: bk(businessId, 'search', 'invoices', q), enabled: enabled && can('invoice:read'), queryFn: () => get<Paginated<InvoiceSummaryDto>>('/invoices', { q, pageSize: 5 }) });
  const customers = useQuery({ queryKey: bk(businessId, 'search', 'customers', q), enabled: enabled && can('customer:read'), queryFn: () => get<Paginated<CustomerDto>>('/customers', { q, pageSize: 5 }) });
  const products = useQuery({ queryKey: bk(businessId, 'search', 'products', q), enabled: enabled && can('catalog:read'), queryFn: () => get<Paginated<ProductDto>>('/products', { q, pageSize: 5 }) });

  const go = (to: string) => {
    setOpen(false);
    setTerm('');
    navigate(to);
  };
  const pages = NAV.flatMap((g) => g.items).filter((i) => !i.permission || can(i.permission));

  return (
    <>
      {/* A search field, not an action button: it looks like the input it opens (quiet border, placeholder text). */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Meta+K Control+K"
        className="hidden h-8 w-60 items-center gap-2 rounded-[var(--btn-radius)] border border-border bg-card px-2.5 text-sm text-muted-foreground transition-colors hover:border-input hover:text-ink-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-btn-focus md:flex"
      >
        <Search className="size-3.5" aria-hidden />
        <span className="flex-1 text-left">Search</span>
        <kbd aria-hidden className="rounded-sm border bg-muted px-1 font-sans text-[11px] leading-4">⌘K</kbd>
      </button>
      <Button variant="ghost" size="icon-sm" className="md:hidden" onClick={() => setOpen(true)} aria-label="Search">
        <Search className="size-4" />
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen} title="Search" description="Search invoices, customers and products, or jump to a page">
        <CommandInput placeholder="Search invoices, customers, products or pages…" value={term} onValueChange={setTerm} />
        <CommandList>
          <CommandEmpty>{q.length < 2 ? 'Type at least 2 characters to search.' : 'No matches.'}</CommandEmpty>
          {can('invoice:write') && (
            <CommandGroup heading="Actions">
              <CommandItem onSelect={() => go('/invoices/new')}>
                <Plus /> New invoice
              </CommandItem>
            </CommandGroup>
          )}
          {invoices.data && invoices.data.data.length > 0 && (
            <CommandGroup heading="Invoices">
              {invoices.data.data.map((i) => (
                <CommandItem key={i.id} value={`inv-${i.id}`} onSelect={() => go(`/invoices/${i.id}`)}>
                  <FileText />
                  <span className="font-medium">{i.numbered ? i.invoiceNumber : 'Draft'}</span>
                  <span className="truncate text-muted-foreground">{i.buyerName}</span>
                  <span className="ml-auto tabular text-xs text-muted-foreground">{money(i.payableAmount, i.documentCurrencyCode)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {customers.data && customers.data.data.length > 0 && (
            <CommandGroup heading="Customers">
              {customers.data.data.map((c) => (
                <CommandItem key={c.id} value={`cus-${c.id}`} onSelect={() => go(`/customers/${c.id}`)}>
                  <Users /> {c.legalName}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {products.data && products.data.data.length > 0 && (
            <CommandGroup heading="Products & services">
              {products.data.data.map((p) => (
                <CommandItem key={p.id} value={`prd-${p.id}`} onSelect={() => go(`/products?q=${encodeURIComponent(p.itemCode)}`)}>
                  <Package /> {p.name} <span className="text-muted-foreground">{p.itemCode}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          <CommandSeparator />
          <CommandGroup heading="Go to">
            {pages.map((p) => (
              <CommandItem key={p.to} value={`page-${p.label}`} onSelect={() => go(p.to)}>
                <p.icon /> {p.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
